import type { PbjPrediction } from "../../../utils/models";
import { scoreGapFromBaseline, WEEKEND_CATEGORY_ID } from "./cumulativeScoring.utils";
import type { PeriodMode } from "./RecommendationsSidebar";
import type { PlanItem, PlanItemCategoryBreakdown, RecommendationType } from "./recommendations.constants";

// FE mirror of the backend greedy planner's HPPD→points→hours math
// (`backend/services/pbj_model/greedy.py`). Pure and React-free so the sidebar's
// render path, its slider-drag re-score, and its "Add all" all share one implementation
// (this piece is as parity-critical as its already-extracted siblings
// `cumulativeScoring.utils` and `planOutcome.utils`).

type RecBucketBreakdown = { categoryId: string; categoryTitle: string; points: number; hppdGap: number };

export type RecData = {
	// Headline HPPD impact — the *primary category's* adjusted-HPPD bump (matches
	// `spend = primary_gap × wage`). Coupled non-primary categories see a smaller
	// bump (see `breakdown[i].hppdGap`) when their denominator differs (weekend
	// recs against Total/RN, which span all days).
	impact: number;
	points: number;
	hours: number;
	// Catch-up rate (per remaining applicable day). null when no applicable days
	// remain. See `calculateRecData` for the per-day-type denominator math.
	hoursCatchup: number | null;
	// One entry per HPPD category the recommendation affects, with the projected
	// point gain AND the per-category HPPD bump (which may differ between cats —
	// e.g. weekend recs bump Weekend HPPD by `primary_gap` but Total HPPD by
	// only `primary_gap × weekendDays / quarterDays` because the hours only
	// land on weekend shifts). Used by the "+X pts" tooltip + the per-cat HPPD
	// labels on the rec card.
	breakdown: RecBucketBreakdown[];
};

// Mirror of `MAX_HPPD_PER_RECOMMENDATION` in `backend/services/pbj_model/constants.py`.
// Caps each rec's primary-category HPPD bump so the slider/projection can't claim
// gains beyond what the BE greedy is willing to suggest. MUST stay in lockstep with
// the BE constant: 3.0 covers the widest possible single band crossing (Total HPPD
// bottom edge 2.722), so the greedy never rejects a facility's first crossing.
export const MAX_HPPD_PER_RECOMMENDATION = 3.0;

// Compute the projected point gain for a recommendation, mirroring the BE greedy's
// per-category delta math (`backend/services/pbj_model/greedy.py`). Key details:
//
//   - `primary_gap = spend / wage` is the adjusted-HPPD bump on the rec's *primary*
//     category (also the headline `+X.XX HPPD` shown on the card).
//   - `hoursTotal = primary_gap × census × days(primary) / primary_scale` translates
//     that into actual reported staffing hours added across the applicable day window
//     (all days for weekday recs, weekend days for weekend recs). The `/ primary_scale`
//     undoes the CMI adjustment so we're working in reported-hours, not adjusted-HPPD.
//   - Each coupled category sees `shift = hoursTotal / (census × days(cat)) × scale(cat)`
//     i.e. the same hours spread over THAT category's denominator. For weekend recs
//     this means Total/RN HPPD see a smaller bump than Weekend HPPD (hours only
//     land on weekend shifts), and previously this code over-counted by applying
//     `primary_gap` to all coupled cats uniformly.
//   - The user-facing `hours` (per-day sustained rate) is `hoursTotal / days(primary)`
//     = `primary_gap × census / primary_scale`, also CMI-reversed.
//   - Band scoring runs against the unbuffered ladder so the projected "+X pts"
//     matches what the Scoring Threshold table shows.
export const calculateRecData = (rec: RecommendationType, spend: number, wage: number, prediction: PbjPrediction, census: number): RecData => {
	const breakdown: RecBucketBreakdown[] = [];
	const primaryCat = prediction.categories.find((c) => c.id === rec.primaryCategoryId);
	const primaryGap = Math.min(wage > 0 ? spend / wage : 0, MAX_HPPD_PER_RECOMMENDATION);

	// Days denominator depends on whether the rec lands on weekends only.
	const recDays = rec.isWeekend ? prediction.weekendDays : prediction.days;
	// Hours of this action that produce `primaryGap` on its primary category. We use
	// the primary cat's denominator + scale to back out hours from the HPPD bump,
	// then re-distribute those same hours across every coupled category.
	const primaryScale = primaryCat?.scale ?? 1;
	const hoursTotal = census > 0 && recDays > 0 && primaryScale > 0 ? (primaryGap * census * recDays) / primaryScale : 0;

	for (const id of rec.impactedIds) {
		const cat = prediction.categories.find((c) => c.id === id);
		if (!cat) continue;
		// Days denominator for THIS category — Weekend HPPD always uses weekend days,
		// Total/RN HPPD always use all days. The recDays-vs-catDays mismatch is what
		// gives weekend recs the smaller cross-category bump.
		const catDays = cat.id === WEEKEND_CATEGORY_ID ? prediction.weekendDays : prediction.days;
		const catScale = cat.scale ?? 1;
		const shift = census > 0 && catDays > 0 ? (hoursTotal / (census * catDays)) * catScale : 0;
		// scoreGapFromBaseline nudges before scoring (BE `_to_steps` parity) so a
		// BE-suggested spend whose shift lands a hair below its band cutoff still
		// credits the crossing here, matching the Action Plan row's cumulative pass.
		const { gain } = scoreGapFromBaseline(cat, cat.rawValue, shift);
		breakdown.push({
			categoryId: id,
			categoryTitle: cat.title,
			points: gain,
			hppdGap: shift,
		});
	}
	const points = breakdown.reduce((sum, b) => sum + b.points, 0);
	// Reverse the CMI adjustment so `hours` is denominated in actual reported
	// staffing hours, not adjusted-HPPD-times-census. Mirrors the BE greedy's
	// `hppd_gap_reported = primary_gap / primary_scale` step.
	const primaryGapReported = primaryScale > 0 ? primaryGap / primaryScale : 0;
	const hours = Math.ceil(primaryGapReported * census);
	// Catch-up: same headline HPPD impact spread over remaining-applicable-day
	// window only. Weekend recs use weekend day counts (hours land on weekend
	// shifts only); weekday recs use calendar day counts. null when no
	// applicable days remain.
	const fullDays = rec.isWeekend ? prediction.quarterWeekendDays : prediction.quarterDays;
	const remainingDays = rec.isWeekend ? prediction.weekendDaysRemaining : prediction.daysRemaining;
	const hoursCatchup = remainingDays > 0 && fullDays > 0 ? Math.ceil((primaryGapReported * census * fullDays) / remainingDays) : null;
	return { impact: primaryGap, points, hours, hoursCatchup, breakdown };
};

// Normalize the slider's raw $/applicable-PPD value into the per-calendar-PPD rate
// shown on the rec card. The effective window depends on the period toggle:
//   - "quarter" mode → spread across the full quarter (sustained rate)
//   - "qtd" mode     → spread across only the remaining catch-up days (higher rate)
// Sum of these displayed values across all selected recs equals the Action Plan's
// "Total PPD Spend" line in the same mode.
export const displaySpendForLabel = (rec: RecommendationType, rawSpend: number, prediction: PbjPrediction, periodMode: PeriodMode): number => {
	const applicableQuarterDays = rec.isWeekend ? prediction.quarterWeekendDays : prediction.quarterDays;
	const effDays = periodMode === "qtd" && prediction.daysRemaining > 0 ? prediction.daysRemaining : prediction.quarterDays;
	if (effDays <= 0 || prediction.quarterDays <= 0) return rawSpend;
	return (rawSpend * applicableQuarterDays) / effDays;
};

// `data.breakdown` carries each touched category's per-category `hppdGap` (the
// baseline-INDEPENDENT shift — it depends only on hours/census/days/scale, not on
// what else is in the plan) plus a standalone-scored point gain. We attach the
// breakdown verbatim to every plan item — including MANUAL slider/drag items — so
// the cumulative pass in ActionItems can re-score each item's marginal points
// against the running prefix. The `points`/`hppdGap` on each bucket are stored in
// the `PlanItemCategoryBreakdown` shape (identical to BE-suggested items), and the
// cumulative pass overrides the `points` at render time; only `hppdGap` is
// load-bearing here and it is order-independent.
export const buildPlanItem = (rec: RecommendationType, spend: number, data: RecData): PlanItem => {
	const breakdown: PlanItemCategoryBreakdown[] = data.breakdown.map((b) => ({
		categoryId: b.categoryId,
		categoryTitle: b.categoryTitle,
		hppdGap: b.hppdGap,
		points: b.points,
	}));
	return {
		id: rec.id,
		titleKey: rec.titleKey,
		spend,
		impact: data.impact,
		points: data.points,
		hours: data.hours,
		hoursCatchup: data.hoursCatchup,
		actionKey: rec.actionKey,
		actionCatchupKey: rec.actionCatchupKey,
		breakdown,
	};
};
